import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useThemeStore } from '../store/theme';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return <ErrorFallback error={this.state.error} />;
        }

        return this.props.children;
    }
}

const ErrorFallback: React.FC<{ error?: Error }> = ({ error }) => {
    const { colors, theme } = useThemeStore();

    const handleRetry = () => {
        window.location.reload();
    };

    return (
        <div className="flex items-center justify-center min-h-screen p-4">
            <div className="text-center max-w-md mx-auto">
                <div className="text-6xl mb-4">😔</div>
                <h2
                    className="text-2xl font-bold mb-2"
                    style={{ color: colors.text }}
                >
                    Something went wrong
                </h2>
                <p
                    className="mb-6 text-sm"
                    style={{ color: colors.gray }}
                >
                    {error?.message || 'An unexpected error occurred while loading this page.'}
                </p>
                <button
                    onClick={handleRetry}
                    className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-105 text-white hover:opacity-90"
                    style={{ backgroundColor: colors.primary }}
                >
                    Try Again
                </button>
            </div>
        </div>
    );
};

export default ErrorBoundary;
